import React, { useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import { useQuery, useMutation } from "@apollo/client";
import { Stack, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Delete } from "@mui/icons-material";
import {
  GetAllContactsByCompanyDocument,
  GetContactsDocument,
  GetContactsQuery,
  SoftDeleteContactDocument,
} from "../../generated/graphql-operations";
import { ConfirmationDialog } from "../ConfirmationDialog";

export interface ContactListProps {
  toolBar: boolean;
  hideColumns?: GridColumnVisibilityModel;
}

const CustomToolbar = () => {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
      <GridToolbarFilterButton />
      <GridToolbarQuickFilter variant="filled" />
    </Stack>
  );
};

export const ContactList = ({
  toolBar,
  hideColumns = undefined,
}: ContactListProps) => {
  const { data, loading, error } = useQuery(GetContactsDocument);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [contactId, setContactId] = useState(0);
  const [softDeleteContact] = useMutation(SoftDeleteContactDocument);

  const navigate = useNavigate();
  const columns: GridColDef<NonNullable<GetContactsQuery["Contacts"][0]>>[] = [
    {
      field: "first_name",
      headerName: "First Name",
      flex: 0.5,
      valueGetter: (params) => params.row.first_name,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      valueGetter: (params) => params.row.last_name,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      valueGetter: (params) => params.row.phone,
    },
    {
      field: "company",
      headerName: "Company",
      flex: 2,
      valueGetter: (params) => params.row.Company?.legal_name,
    },
    {
      field: "position",
      headerName: "Position",
      flex: 2,
      valueGetter: (params) =>
        JSON.parse(params.row.content ?? "{}").position ?? "",
    },
    {
      field: "deals",
      headerName: "Active Deals",
      align: "center",
      flex: 1,
      valueGetter: (params) => params.row.DealsContacts.length,
    },
    {
      field: "buttons",
      headerName: "",
      flex: 0.6,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => {
            setContactId(params.row.id);
            setOpenConfirmation(true);
          }}
        >
          <Delete sx={{ color: "red" }} />
        </IconButton>
      ),
    },
  ];
  return (
    <>
      <DataGrid
        rows={data?.Contacts || []}
        columns={columns}
        loading={loading || error !== undefined}
        columnVisibilityModel={hideColumns}
        onCellClick={(params, event) => {
          if (params.field === "buttons") {
            event.stopPropagation();
            return;
          }

          if (!event.ctrlKey || params.field !== "buttons") {
            navigate(`/Contacts/${params.row.id}`);
          }
        }}
        components={toolBar ? { Toolbar: CustomToolbar } : {}}
      />
      <ConfirmationDialog
        open={openConfirmation}
        setOpen={setOpenConfirmation}
        title="Delete Contact"
        description="Are you sure you want to delete this contact?"
        confirmText="Yes"
        declineText="No"
        onConfirm={async () => {
          await softDeleteContact({
            variables: {
              id: contactId,
            },
            refetchQueries: [
              GetContactsDocument,
              GetAllContactsByCompanyDocument,
            ],
          });
        }}
      />
    </>
  );
};

export default ContactList;
